import React from 'react';
import { apiFetch } from '../../lib/api';

/**
 * Le filtre anti-robots de Cloudflare (Turnstile) sur la page de connexion.
 *
 * Le navigateur resout un defi Cloudflare et en rapporte un jeton, que le serveur fait verifier avant
 * de regarder le mot de passe. Un robot n'obtient pas ce jeton : sa tentative n'atteint jamais la base.
 * Un visiteur normal ne voit en general qu'une case qui se coche toute seule.
 *
 * LE COMPOSANT EST MUET TANT QUE LES CLES NE SONT PAS POSEES sur l'environnement Cloudflare : il
 * n'affiche rien et laisse la connexion fonctionner comme avant. C'est le serveur qui decide, et il
 * repond ici par /public/turnstile — on ne met pas la cle de site en dur dans le code, sans quoi il
 * faudrait redeployer l'application pour changer de widget.
 */
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// QUAND LA VERIFICATION NE SE CHARGE PAS, ON NE FERME PAS LA PORTE. Le serveur, lui, laisse passer
// quand Cloudflare est injoignable (la limitation de tentatives continue de proteger) : si l'ecran
// bloquait le bouton dans ce cas, une panne de Cloudflare enfermerait dehors des clients legitimes.
// Un robot, lui, n'y gagne rien : sans jeton, le serveur refuse quand meme sa connexion.
const MESSAGE_PANNE = "La vérification anti-robots n'a pas pu se charger. Vous pouvez tout de même essayer de vous connecter.";

declare global {
    interface Window { turnstile?: any }
}

/** Charge le script Cloudflare une seule fois, meme si le composant est remonte. */
let chargement: Promise<void> | null = null;
const chargerScript = () => {
    if (window.turnstile) return Promise.resolve();
    if (chargement) return chargement;
    chargement = new Promise<void>((resoudre, rejeter) => {
        const script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resoudre();
        script.onerror = () => { chargement = null; rejeter(new Error('script Turnstile injoignable')); };
        document.head.appendChild(script);
    });
    return chargement;
};

export interface TurnstileHandle {
    /** Le jeton a envoyer avec la connexion, ou '' si le filtre est inactif. */
    jeton: string;
    /** true tant que le widget est affiche sans jeton : le bouton d'envoi doit attendre. */
    enAttente: boolean;
    /** A appeler apres un echec de connexion : le jeton consomme est remplace par un neuf. */
    reinitialiser: () => void;
}

export function useTurnstile(): TurnstileHandle & { rendu: React.ReactNode } {
    const conteneur = React.useRef<HTMLDivElement | null>(null);
    const widgetId = React.useRef<string | null>(null);
    const [siteKey, setSiteKey] = React.useState('');
    const [jeton, setJeton] = React.useState('');
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        let vivant = true;
        apiFetch('/public/turnstile')
            .then((r: any) => { if (vivant && r?.actif && r?.siteKey) setSiteKey(String(r.siteKey)); })
            .catch(() => { /* filtre indisponible : on laisse la connexion telle quelle */ });
        return () => { vivant = false; };
    }, []);

    React.useEffect(() => {
        if (!siteKey || !conteneur.current) return;
        let vivant = true;
        chargerScript()
            .then(() => {
                if (!vivant || !conteneur.current || widgetId.current) return;
                widgetId.current = window.turnstile.render(conteneur.current, {
                    sitekey: siteKey,
                    callback: (t: string) => setJeton(t),
                    'expired-callback': () => setJeton(''),
                    'error-callback': () => { setJeton(''); setErreur(MESSAGE_PANNE); },
                    theme: 'auto',
                });
            })
            .catch(() => setErreur(MESSAGE_PANNE));
        return () => {
            vivant = false;
            if (widgetId.current && window.turnstile) {
                try { window.turnstile.remove(widgetId.current); } catch { /* deja retire */ }
                widgetId.current = null;
            }
        };
    }, [siteKey]);

    const reinitialiser = React.useCallback(() => {
        setJeton('');
        if (widgetId.current && window.turnstile) {
            try { window.turnstile.reset(widgetId.current); } catch { /* rien a reinitialiser */ }
        }
    }, []);

    // Le widget occupe sa place des le depart : sans cela, le bouton « Se connecter » sauterait vers le
    // bas au moment ou Cloudflare finit de se charger, juste sous le doigt de qui allait cliquer.
    const rendu = siteKey ? (
        <div className="mt-1 flex flex-col items-center">
            <div ref={conteneur} className="min-h-[65px] flex justify-center" />
            {erreur && (
                <p className="mt-1 text-[12px] text-content-muted text-pretty text-center">{erreur}</p>
            )}
        </div>
    ) : null;

    return { jeton, enAttente: Boolean(siteKey) && !jeton && !erreur, reinitialiser, rendu };
}
